// PurchaseInventoryTransactionList.jsx
import React, { useEffect, useState } from "react";
import {
  Edit, Trash2, Plus, Building, User, Package, Warehouse, Download,
  Upload, Filter, Search, RefreshCw, Eye, Calendar, DollarSign
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./PurchaseInventoryTransactionList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const PurchaseInventoryTransactionList = () => {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [userCompany, setUserCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({ product_id: "", warehouse_id: "", search: "", status: "" });
  const [stats, setStats] = useState({ total: 0, totalQuantity: 0, totalValue: 0 });
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Inventory transactions (type = purchase)
      const invRes = await axios.get(`${url}/api/inventorytransaction`, {
        params: { status: filters.status }
      });
      const allTxns = invRes.data.data || [];
      const purchaseTxns = allTxns.filter(tx => {
        const typeObj = tx.transaction_type_id;
        return typeObj && typeof typeObj === 'object' && typeObj.transaction_type?.toLowerCase() === 'purchase';
      });

      // 2. Purchase orders
      const poRes = await axios.get(`${url}/api/purchaseorders`, {
        params: { limit: 500 }
      });
      const purchaseOrders = poRes.data.data || [];

      // Transform purchase orders into rows (one per item)
      const poRows = [];
      for (const order of purchaseOrders) {
        const orderDate = order.order_date || order.created_at;
        const orderWarehouse = order.warehouse_id;
        const orderStatus = order.status;
        for (const item of order.items) {
          poRows.push({
            _type: 'purchase_order',
            reference_id: order.reference_id,
            invoice_no: order.order_no,
            product_id: item.product_id,
            quantity: item.quantity,
            unit_cost: item.unit_price,
            total_cost: item.total_price,
            transaction_date: orderDate,
            status: orderStatus,
            warehouse_id: orderWarehouse,
            created_by: order.created_by,
            original_order: order
          });
        }
      }

      let combined = [...purchaseTxns.map(t => ({ ...t, _type: 'purchase' })), ...poRows];
      combined.sort((a, b) => new Date(b.transaction_date || b.created_at) - new Date(a.transaction_date || a.created_at));
      setAllRows(combined);
      setFilteredData(combined);

      // Stats: for purchases, quantities are already positive; use absolute for safety
      const totalQty = combined.reduce((sum, r) => sum + Math.abs(r.quantity || 0), 0);
      const totalVal = combined.reduce((sum, r) => sum + Math.abs(r.total_cost || 0), 0);
      setStats({ total: combined.length, totalQuantity: totalQty, totalValue: totalVal });

      if (combined[0]?.company_id) setUserCompany(combined[0].company_id);
      else if (user?.company) setUserCompany(user.company);
    } catch (err) {
      toast.error("Failed to load purchase data");
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const productsRes = await axios.get(`${url}/api/products/dropdown`);
      if (productsRes.data.success) setProducts(productsRes.data.data || []);
      const warehousesRes = await axios.get(`${url}/api/warehouse/dropdown`);
      if (warehousesRes.data.success) setWarehouses(warehousesRes.data.data || []);
    } catch (err) { }
  };

  useEffect(() => { fetchData(); fetchDropdowns(); }, []);
  useEffect(() => { applyFilters(); }, [filters, allRows]);

  const applyFilters = () => {
    let filtered = [...allRows];
    if (filters.product_id) {
      filtered = filtered.filter(row => {
        const pid = row.product_id?._id || row.product_id;
        return pid === filters.product_id;
      });
    }
    if (filters.warehouse_id) {
      filtered = filtered.filter(row => (row.warehouse_id?._id || row.warehouse_id) === filters.warehouse_id);
    }
    if (filters.search) {
      const s = filters.search.toLowerCase();
      filtered = filtered.filter(row => {
        const ref = row.reference_id || '';
        const inv = row.invoice_no || '';
        const prodName = row.product_id?.product_name || '';
        return ref.toLowerCase().includes(s) || inv.toLowerCase().includes(s) || prodName.toLowerCase().includes(s);
      });
    }
    if (filters.status) {
      filtered = filtered.filter(row => (row.status || 'active') === filters.status);
    }
    setFilteredData(filtered);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

  const getDisplayFields = (row) => {
    const product = row.product_id || {};
    const warehouse = row.warehouse_id || {};
    return {
      ref: row.reference_id || '',
      invNo: row.invoice_no || '',
      productName: product.product_name || 'Unknown',
      productCode: product.product_code || '',
      warehouseName: warehouse.warehouse_name || '',
      qty: row.quantity || 0,
      unitCost: row.unit_cost || 0,
      total: row.total_cost || 0,
      date: row.transaction_date,
      status: row.status || 'active',
      creator: row.created_by?.name || row.created_by?.Usercomp_sure_name || 'System'
    };
  };

  const handleExport = () => {
    if (filteredData.length === 0) return toast.warning("No data");
    const headers = ["Type", "Reference", "Invoice/Order", "Product", "Warehouse", "Qty", "Unit Cost", "Total", "Date", "Status"];
    const rows = filteredData.map(row => {
      const f = getDisplayFields(row);
      return [row._type === 'purchase_order' ? 'Purchase Order' : 'Purchase', f.ref, f.invNo, f.productName, f.warehouseName, Math.abs(f.qty), formatCurrency(f.unitCost), formatCurrency(f.total), formatDate(f.date), f.status];
    });
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `purchase-all-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Exported");
  };

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div style={{ padding: 20, background: '#f5f5f5', minHeight: '100vh' }}>
      <style>{`
        .header { display: flex; justify-content: space-between; background: white; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
        .stats-bar { display: grid; grid-template-columns: repeat(4,1fr); gap: 15px; margin-bottom: 20px; }
        .stat-item { background: white; padding: 15px; border-radius: 8px; display: flex; align-items: center; gap: 12px; }
        .stat-value { font-size: 24px; font-weight: bold; }
        .stat-label { font-size: 12px; color: #666; }
        .filters-container { background: white; border-radius: 8px; margin-bottom: 20px; }
        .filter-header { padding: 12px 20px; background: #f8f9fa; cursor: pointer; display: flex; gap: 8px; }
        .filter-fields { display: flex; flex-wrap: wrap; gap: 15px; padding: 20px; }
        .filter-field { flex: 1; min-width: 200px; }
        .filter-field input, .filter-select { width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 6px; }
        .clear-filters-btn { background: #dc3545; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer; }
        .actions-bar { display: flex; justify-content: space-between; margin-bottom: 20px; }
        .export-button { background: #28a745; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer; display: flex; gap: 6px; align-items: center; }
        .table-container { background: white; border-radius: 8px; overflow-x: auto; }
        .district-table { width: 100%; border-collapse: collapse; }
        .district-table th, .district-table td { padding: 12px 15px; text-align: left; border-bottom: 1px solid #eee; }
        .district-table th { background: #170f48; color: white; }
        .district-table td { color: #333; }
        .status-badge { display: inline-block; padding: 4px 8px; border-radius: 20px; font-size: 0.75rem; }
        .status-badge.active { background: #d4edda; color: #155724; }
        .pagination { display: flex; justify-content: center; gap: 10px; margin-top: 20px; }
        .page-btn, .pagination-btn { padding: 6px 12px; border: 1px solid #ddd; background: white; cursor: pointer; border-radius: 4px; }
        .page-btn.active { background: #007bff; color: white; }
      `}</style>

      <div className="header">
        <div>
          <h2 style={{ color: 'blue', WebkitTextFillColor: 'blue' }}>
            Purchase Transactions & Orders
          </h2>
          {userCompany && (
            <p style={{ color: 'blue' }}>
              <Building size={14} /> {userCompany.company_name} ({userCompany.company_code})
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => fetchData()} disabled={refreshing}>Refresh</button>
        </div>
      </div>

      <div className="stats-bar">
        <div className="stat-item" style={{ color: "blue" }}>
          <Package color="blue" />
          <div>
            <span className="stat-value" style={{ color: "blue", WebkitTextFillColor: "blue" }}>
              {stats.total}
            </span>
            <span className="stat-label" style={{ color: "blue", WebkitTextFillColor: "blue" }}>
              Total Records
            </span>
          </div>
        </div>

        <div className="stat-item" style={{ color: "blue" }}>
          <div>
            <span className="stat-value" style={{ color: "blue", WebkitTextFillColor: "blue" }}>
              {stats.totalQuantity}
            </span>
            <span className="stat-label" style={{ color: "blue", WebkitTextFillColor: "blue" }}>
              Total Qty
            </span>
          </div>
        </div>

        <div className="stat-item" style={{ color: "blue" }}>
          <div>
            <span className="stat-value" style={{ color: "blue", WebkitTextFillColor: "blue" }}>
              {formatCurrency(stats.totalValue)}
            </span>
            <span className="stat-label" style={{ color: "blue", WebkitTextFillColor: "blue" }}>
              Total Value
            </span>
          </div>
        </div>
      </div>

      <div className="filters-container">
        <div className="filter-header" onClick={() => setShowFilters(!showFilters)}>Filters {showFilters ? '▲' : '▼'}</div>
        {showFilters && (<div className="filter-fields">
          <div className="filter-field"><input type="text" placeholder="Search" value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} /></div>
          <select className="filter-select" value={filters.product_id} onChange={e => setFilters({ ...filters, product_id: e.target.value })}>
            <option value="">All Products</option>
            {products.map(p => <option key={p._id} value={p._id}>{p.product_code} - {p.product_name}</option>)}
          </select>
          <select className="filter-select" value={filters.warehouse_id} onChange={e => setFilters({ ...filters, warehouse_id: e.target.value })}>
            <option value="">All Warehouses</option>
            {warehouses.map(w => <option key={w._id} value={w._id}>{w.warehouse_code} - {w.warehouse_name}</option>)}
          </select>
          <button className="clear-filters-btn" onClick={() => setFilters({ product_id: "", warehouse_id: "", search: "", status: "" })}>Clear</button>
        </div>)}
      </div>

      <div className="actions-bar"><button className="export-button" onClick={handleExport}><Download size={16} /> Export CSV</button></div>

      <div className="table-container">
        <table className="district-table">
          <thead>
            <tr>
              <th>Type</th><th>Reference ID</th><th>Invoice/Order No</th><th>Product</th><th>Warehouse</th><th>Qty</th><th>Unit Cost</th><th>Total</th><th>Transaction Date</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr><td colSpan="10" style={{ textAlign: 'center', padding: 40 }}>No data</td></tr>
            ) : (
              paginatedData.map((row, idx) => {
                const f = getDisplayFields(row);
                return (
                  <tr key={idx}>
                    <td>{row._type === 'purchase_order' ? '📦 Purchase Order' : '📄 Purchase'}</td>
                    <td><strong>{f.ref}</strong></td>
                    <td>{f.invNo}</td>
                    <td>{f.productName}<br /><small>{f.productCode}</small></td>
                    <td>{f.warehouseName}</td>
                    <td style={{ textAlign: 'right' }}>{Math.abs(f.qty)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(f.unitCost)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(f.total)}</td>
                    <td>{formatDate(f.date)}</td>
                    <td><span className={`status-badge ${f.status === 'active' || f.status === 'completed' ? 'active' : ''}`}>{f.status}</span></td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="pagination">
          <button className="pagination-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
          {[...Array(totalPages)].map((_, i) => (
            <button key={i} className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
          ))}
          <button className="pagination-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
};

export default PurchaseInventoryTransactionList;